import React, { useContext, useEffect, useRef } from 'react';
import { ThemeContext, type DefaultTheme } from 'styled-components';
import { usePrefersReducedMotion } from '../../_internal/usePrefersReducedMotion';
import { type BuddyColors, drawBuddy, drawShadow } from './buddyEngine';
import { type BuddiesAct, sceneAt, viewportOf } from './buddyScene';
import {
  BUDDIES_BUSY,
  BUDDIES_DEFAULT_SIZE,
  BUDDIES_FIT_ORB,
  BUDDIES_PAIR,
  BUDDIES_STILL_TIME,
  type BuddiesFit,
  type BuddiesWho,
} from './Buddies.constants';
import { Canvas } from './Buddies.styled';

export interface BuddiesProps {
  who?: BuddiesWho;
  /** A behaviour, or `busy` for the long working cycle. */
  act?: BuddiesAct;
  size?: number;
  fit?: BuddiesFit;
  mirrored?: boolean;
  className?: string;
  dataTestId?: string;
}

export const buddyColorsOf = (theme: DefaultTheme | undefined): BuddyColors => {
  const colors = theme?.colors ?? {};
  return {
    ink: colors.onSurface ?? '#FFFFFF',
    face: colors.surface ?? '#0E0F18',
    blush: colors.error ?? '#FF8A7D',
    shadow: colors.surfaceContainerHigh ?? '#262738',
    spark: colors.caution ?? '#FFDF9E',
    accent: colors.badge ?? '#7370B0',
    muted: colors.onSurfaceVariant ?? '#C4C6D6',
  };
};

const Buddies: React.FC<BuddiesProps> = ({
  who = BUDDIES_PAIR,
  act = 'idle',
  size = BUDDIES_DEFAULT_SIZE,
  fit = BUDDIES_FIT_ORB,
  mirrored = false,
  className,
  dataTestId = 'buddies',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const theme = useContext(ThemeContext) as DefaultTheme | undefined;
  const reducedMotion = usePrefersReducedMotion();
  // One clock for the whole busy cycle, so tip routines in between don't restart it at its first act.
  const busyStart = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const g = canvas?.getContext('2d');
    if (!canvas || !g) return undefined;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const colors = buddyColorsOf(theme);
    const { unit, groundY } = viewportOf(size, who, fit);

    const draw = (t: number) => {
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, size, size);
      g.setTransform(unit * dpr * (mirrored ? -1 : 1), 0, 0, unit * dpr, (size / 2) * dpr, groundY * dpr);
      const { poses, effects } = sceneAt(who, act, t, fit);
      for (const pose of poses) drawShadow(g, pose, colors);
      for (const pose of poses) drawBuddy(g, pose, t, colors);
      for (const effect of effects) effect(g, colors);
    };

    if (reducedMotion) {
      draw(BUDDIES_STILL_TIME);
      return undefined;
    }

    let frame = 0;
    let visible = true;
    busyStart.current ??= performance.now();
    const start = act === BUDDIES_BUSY ? busyStart.current : performance.now();
    const loop = (now: number) => {
      draw((now - start) / 1000);
      frame = window.requestAnimationFrame(loop);
    };
    const resume = () => {
      window.cancelAnimationFrame(frame);
      if (visible && !document.hidden) frame = window.requestAnimationFrame(loop);
    };
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(([entry]) => {
            visible = entry?.isIntersecting ?? true;
            resume();
          });
    observer?.observe(canvas);
    document.addEventListener('visibilitychange', resume);
    resume();
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      document.removeEventListener('visibilitychange', resume);
    };
  }, [act, fit, mirrored, reducedMotion, size, theme, who]);

  return (
    <Canvas
      ref={canvasRef}
      $size={size}
      className={className}
      role="presentation"
      aria-hidden="true"
      data-testid={dataTestId}
      data-act={act}
    />
  );
};

export default Buddies;
